import Phaser from "phaser";
import { worldToIso, isoDepth } from "../core/iso";
import type { EquipmentSlots } from "../core/types";
import { CharacterVisual } from "../visual/CharacterVisual";
import type { Direction } from "../visual/AssetManifest";

export interface Origin {
  ox: number;
  oy: number;
}

/**
 * EPI controlable. LÓGICA de movimiento (x,y en tiles, velocidad, colisión con
 * bordes) — intacta. La REPRESENTACIÓN se delega en CharacterVisual, que usa el
 * spritesheet real si existe o el placeholder vectorial si no (ART_SPEC).
 *
 * Esta clase solo traduce el movimiento a dirección/estado de animación; no
 * decide cómo se dibuja EPI.
 */
export class Player {
  x: number;
  y: number;
  speed = 3.4;

  readonly container: Phaser.GameObjects.Container;
  private visual: CharacterVisual;
  private moving = false;
  private dir: Direction = "SW";
  private ew: "E" | "W" = "W";
  private ns: "N" | "S" = "S";

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.x = x;
    this.y = y;
    this.visual = new CharacterVisual(scene, "epi");
    this.container = this.visual.container;
  }

  /** Muestra/oculta los EPIs equipados (placeholder vectorial). */
  applyEquipment(slots: EquipmentSlots): void {
    this.visual.applyEquipment(slots);
  }

  move(dx: number, dy: number, dtSec: number, bounds: { w: number; h: number }): void {
    const len = Math.hypot(dx, dy);
    this.moving = len > 0.001;
    if (this.moving) {
      this.x = Phaser.Math.Clamp(this.x + (dx / len) * this.speed * dtSec, 0, bounds.w - 1);
      this.y = Phaser.Math.Clamp(this.y + (dy / len) * this.speed * dtSec, 0, bounds.h - 1);
      this.updateDirection(dx, dy);
    }
  }

  /** Deriva la dirección isométrica (4 dir) del desplazamiento en pantalla. */
  private updateDirection(dx: number, dy: number): void {
    const dsx = dx - dy; // eje pantalla horizontal (E/O)
    const dsy = dx + dy; // eje pantalla vertical (N/S)
    if (dsx > 0) this.ew = "E";
    else if (dsx < 0) this.ew = "W";
    if (dsy > 0) this.ns = "S";
    else if (dsy < 0) this.ns = "N";
    this.dir = `${this.ns}${this.ew}` as Direction;
  }

  sync(origin: Origin): void {
    const { sx, sy } = worldToIso(this.x, this.y);
    this.container.setPosition(origin.ox + sx, origin.oy + sy);
    this.container.setDepth(isoDepth(this.x, this.y) + 1);
    this.visual.setDirection(this.dir);
    this.visual.setState(this.moving ? "walk" : "idle");
    this.visual.update(0, this.moving);
  }
}
